/**
 * Platform admin — SAM Screen Rules (the card rulebook, `sam_cards.presentation_policy`).
 *
 * A no-JSON editor for what the user's screen does after each SAM reply: rules in the order they
 * are checked, on/off switches, a plain-language editor per rule, save with server validation, and
 * the save history with restore. Pickers come from the backend's `editor_schema` (GET on the key);
 * saving uses the generic configurations PUT, which validates and snapshots each save.
 */
import React, { useEffect, useMemo, useState } from 'react';
import PageShell from '@/components/Layout/PageShell';
import {
  useGetConfigurationEntryQuery,
  useGetPromptVersionsQuery,
  useUpdateCatalogueConfigMutation,
  type RulebookSchema,
  type RulebookVersion,
} from '@/redux/internal-services/platformAdminApi';
import { getErrorDetail } from '@/utils/apiError';
import RuleEditor, { useRulebookLabels } from '@/components/Rulebook/RuleEditor';
import { freePriority, whenToRows, type Decision, type Rule, type Rulebook, type Toast } from '@/components/Rulebook/rulebookModel';

const KEY = 'sam_cards.presentation_policy';
const btn = 'min-h-11 rounded-lg px-3 text-sm';

// ── One-line summaries for the list ─────────────────────────────────────────

const ConditionChips: React.FC<{ when: Record<string, unknown> }> = ({ when }) => {
  const L = useRulebookLabels();
  const rows = whenToRows(when);
  if (!rows.length) return <span className="text-xs text-ui-text3">{L.t('rulebook.list.always')}</span>;
  const show = (fact: string, v: unknown) => {
    const isCard = fact.endsWith('_card') || fact === 'queued_cards';
    const one = (x: unknown) => (typeof x === 'boolean' ? L.t(x ? 'rulebook.editor.yes' : 'rulebook.editor.no') : isCard ? L.card(String(x)) : L.value(String(x)));
    return Array.isArray(v) ? v.map(one).join(' / ') : one(v);
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      {rows.map((r) => (
        <span key={r.fact} className="rounded-md border border-ui-border bg-ui-subtle px-2 py-0.5 text-xs text-ui-text2">
          <span className="text-ui-text3">{L.fact(r.fact)}</span>{' '}
          {r.op === 'exists' ? L.t(r.value ? 'rulebook.editor.hasValue' : 'rulebook.editor.hasNoValue') : <>{L.op(r.op)} <span className="font-medium text-ui-text">{show(r.fact, r.value)}</span></>}
        </span>
      ))}
    </div>
  );
};

const DecisionSummary: React.FC<{ d: Decision }> = ({ d }) => {
  const L = useRulebookLabels();
  const fb = d.feedback as Toast | undefined;
  const mc = d.multi_create as string | undefined;
  return (
    <div className="text-xs text-ui-text2 flex flex-wrap gap-x-3 gap-y-1">
      <span className="font-medium text-ui-text">{L.action(d.action)}</span>
      {mc && mc !== 'LAST_ONLY' && <span>· {L.t(`rulebook.multi.${mc}`, { defaultValue: mc })}</span>}
      {fb && <span>· {L.t('rulebook.list.toast')}: “{L.message(fb.message_code)}”</span>}
      {d.prefer_card != null && <span>· {L.option('prefer_card')}: {(Array.isArray(d.prefer_card) ? d.prefer_card : [d.prefer_card]).map((c) => L.card(String(c))).join(', ')}</span>}
    </div>
  );
};

// ── "See it in action" — a sample message per rule, opened in AI Chat (pre-filled, not sent) ──

const RuleExample: React.FC<{ ruleId: string }> = ({ ruleId }) => {
  const L = useRulebookLabels();
  const [open, setOpen] = useState(false);
  const k = `rulebook.examples.${ruleId}`;
  const has = L.t(`${k}.expect`, { defaultValue: '' }) !== '';
  const prompt = L.t(`${k}.prompt`, { defaultValue: '' });
  return (
    <div>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="min-h-11 text-xs font-medium text-brand-dark hover:underline">
        {open ? L.t('rulebook.example.hide') : `▸ ${L.t('rulebook.example.show')}`}
      </button>
      {open && (
        <div className="rounded-lg border border-brand-dark/20 bg-brand-light/30 p-3 grid gap-2 text-xs text-ui-text">
          {!has ? <p className="text-ui-text3">{L.t('rulebook.example.none')}</p> : (
            <>
              <p><span className="font-medium text-ui-text2">{L.t('rulebook.example.setup')}: </span>{L.t(`${k}.setup`)}</p>
              {prompt
                ? <p><span className="font-medium text-ui-text2">{L.t('rulebook.example.say')}: </span><span className="font-medium">“{prompt}”</span></p>
                : <p className="text-ui-text3">{L.t('rulebook.example.notTypeable')}</p>}
              <p><span className="font-medium text-ui-text2">{L.t('rulebook.example.expect')}: </span>{L.t(`${k}.expect`)}</p>
              {prompt && (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <a href={`/chat?ask=${encodeURIComponent(prompt)}`} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center rounded-lg bg-brand-dark px-3 text-xs font-medium text-white">
                    {L.t('rulebook.example.try')} ↗
                  </a>
                  <span className="text-ui-text3 min-w-0">{L.t('rulebook.example.tryHelp')}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── History panel ────────────────────────────────────────────────────────────

const HistoryPanel: React.FC<{ onRestore: (r: Rulebook) => void; onClose: () => void }> = ({ onRestore, onClose }) => {
  const L = useRulebookLabels();
  const { data, isLoading, isError } = useGetPromptVersionsQuery({ key: KEY, setting: 'policy', limit: 20 }, { refetchOnMountOrArgChange: true });
  const versions = (data?.data ?? []) as unknown as RulebookVersion[];
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="rb-history-title">
      <div className="bg-ui-card w-full sm:max-w-xl max-h-[85vh] flex flex-col rounded-t-2xl sm:rounded-2xl shadow-xl">
        <div className="flex items-start justify-between gap-3 p-4 sm:p-5 border-b border-ui-border">
          <div>
            <h2 id="rb-history-title" className="text-base font-semibold text-ui-text">{L.t('rulebook.history.title')}</h2>
            <p className="text-xs text-ui-text3 mt-0.5">{L.t('rulebook.history.subtitle')}</p>
          </div>
          <button type="button" onClick={onClose} className="min-h-11 min-w-11 rounded-lg text-ui-text2 hover:bg-ui-hover" aria-label={L.t('rulebook.editor.close')}>✕</button>
        </div>
        <div className="overflow-y-auto p-4 sm:p-5 grid gap-2">
          {isLoading && <p className="text-sm text-ui-text3">{L.t('rulebook.loading')}</p>}
          {isError && <p className="text-sm text-danger">{L.t('rulebook.history.error')}</p>}
          {!isLoading && !isError && !versions.length && <p className="text-sm text-ui-text3">{L.t('rulebook.history.empty')}</p>}
          {versions.map((v) => {
            const rb = v.value as unknown as Rulebook;
            return (
              <div key={v.saved_at} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-ui-border p-3">
                <div className="min-w-0 text-sm">
                  <div className="text-ui-text font-medium">{L.t('rulebook.version', { version: rb?.version ?? '?' })} · {new Date(v.saved_at).toLocaleString()}</div>
                  <div className="text-xs text-ui-text3">{L.t('rulebook.history.ruleCount', { count: rb?.rules?.length ?? 0 })}</div>
                </div>
                <button type="button" onClick={() => onRestore(rb)} className={`${btn} border border-ui-border text-ui-text hover:border-brand-dark`}>
                  {L.t('rulebook.history.restore')}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const RulebookPage: React.FC = () => {
  const L = useRulebookLabels();
  const { data, isLoading, isError, refetch } = useGetConfigurationEntryQuery(KEY, { refetchOnMountOrArgChange: true });
  const [save, { isLoading: saving }] = useUpdateCatalogueConfigMutation();

  const server = data?.data?.system_value as unknown as Rulebook | undefined;
  const schema = data?.data?.editor_schema as RulebookSchema | undefined;

  const [rules, setRules] = useState<Rule[] | null>(null);
  const [loadedVersion, setLoadedVersion] = useState<number | null>(null);
  const [defaultDecision, setDefaultDecision] = useState<Decision | null>(null);
  const [editing, setEditing] = useState<{ rule: Rule; index: number | null } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [status, setStatus] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  // Load the server copy into the draft — first load, after Discard, and whenever a save brings a
  // new version (the refetch can land after the draft was cleared, so key on the version).
  useEffect(() => {
    if (server && (rules === null || server.version !== loadedVersion)) {
      setRules([...server.rules].sort((a, b) => a.priority - b.priority));
      setDefaultDecision(server.default_decision);
      setLoadedVersion(server.version);
    }
  }, [server, rules, loadedVersion]);

  const dirty = useMemo(() => {
    if (!server || !rules || !defaultDecision) return false;
    const norm = (rs: Rule[]) => JSON.stringify([...rs].sort((a, b) => a.priority - b.priority));
    return norm(rules) !== norm(server.rules) || JSON.stringify(defaultDecision) !== JSON.stringify(server.default_decision);
  }, [server, rules, defaultDecision]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  if (isLoading || (server && !rules)) {
    return <PageShell header={<h1 className="text-xl font-bold text-ui-text">{L.t('rulebook.title')}</h1>}><p className="p-6 text-sm text-ui-text3">{L.t('rulebook.loading')}</p></PageShell>;
  }
  if (isError || !server || !schema || !rules || !defaultDecision) {
    return <PageShell header={<h1 className="text-xl font-bold text-ui-text">{L.t('rulebook.title')}</h1>}><p className="p-6 text-sm text-danger">{L.t('rulebook.loadError')}</p></PageShell>;
  }

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= rules.length) return;
    const next = rules.map((r) => ({ ...r }));
    [next[i].priority, next[j].priority] = [next[j].priority, next[i].priority];
    setRules(next.sort((a, b) => a.priority - b.priority));
  };
  const toggle = (i: number) => setRules(rules.map((r, k) => (k === i ? { ...r, enabled: r.enabled === false ? undefined : false } : r)));
  const remove = (i: number) => { setRules(rules.filter((_, k) => k !== i)); setConfirmDelete(null); };
  const duplicate = (i: number) => {
    const src = rules[i];
    const ids = new Set(rules.map((r) => r.rule_id));
    let id = `${src.rule_id}-copy`; let n = 2;
    while (ids.has(id)) id = `${src.rule_id}-copy-${n++}`;
    setEditing({ rule: { ...JSON.parse(JSON.stringify(src)), rule_id: id, priority: freePriority(rules, src.priority) }, index: null });
  };
  const addNew = () => setEditing({
    rule: { rule_id: '', priority: freePriority(rules, Math.max(0, ...rules.map((r) => r.priority))), when: {}, decision: { action: schema.actions.includes('NO_UI_CHANGE') ? 'NO_UI_CHANGE' : schema.actions[0] } },
    index: null,
  });
  const applyEdit = (r: Rule) => {
    const next = editing?.index != null ? rules.map((x, k) => (k === editing.index ? r : x)) : [...rules, r];
    setRules(next.sort((a, b) => a.priority - b.priority));
    setEditing(null);
  };

  const discard = () => { setRules(null); setDefaultDecision(null); setStatus(null); refetch(); };
  const doSave = async () => {
    setStatus(null);
    const version = (server.version || 0) + 1;
    try {
      await save({ key: KEY, value: { version, rules, default_decision: defaultDecision } }).unwrap();
      setStatus({ kind: 'ok', text: L.t('rulebook.saved', { version }) });
      refetch();
    } catch (err) {
      setStatus({ kind: 'error', text: `${L.t('rulebook.saveFailed')} ${getErrorDetail(err) ?? ''}`.trim() });
    }
  };
  const restore = (rb: Rulebook) => {
    setRules([...(rb.rules ?? [])].sort((a, b) => a.priority - b.priority));
    if (rb.default_decision) setDefaultDecision(rb.default_decision);
    setShowHistory(false);
    setStatus({ kind: 'ok', text: L.t('rulebook.restored', { version: rb.version }) });
  };

  const header = (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-xl font-bold text-ui-text">{L.t('rulebook.title')}</h1>
        <p className="text-sm text-ui-text2 mt-1">{L.t('rulebook.subtitle')}</p>
        <p className="text-xs text-ui-text3 mt-1">
          {L.t('rulebook.version', { version: server.version })}
          {dirty && <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-amber-700">{L.t('rulebook.unsaved')}</span>}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => setShowHistory(true)} className={`${btn} border border-ui-border text-ui-text hover:border-brand-dark`}>{L.t('rulebook.history.open')}</button>
        <button type="button" onClick={discard} disabled={!dirty || saving} className={`${btn} border border-ui-border text-ui-text disabled:opacity-40`}>{L.t('rulebook.discard')}</button>
        <button type="button" onClick={doSave} disabled={!dirty || saving} className={`${btn} bg-brand-dark font-medium text-white disabled:opacity-40`}>
          {saving ? L.t('rulebook.saving') : L.t('rulebook.save')}
        </button>
      </div>
    </div>
  );

  return (
    <PageShell maxWidth="max-w-4xl" header={header}>
      <div className="grid gap-4 py-4">
        <div className="rounded-xl border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-800">{L.t('rulebook.warning')}</div>
        {status && (
          <div role="status" className={`rounded-xl px-4 py-3 text-sm ${status.kind === 'ok' ? 'border border-green-300/60 bg-green-50 text-green-800' : 'border border-red-300/60 bg-red-50 text-red-700'}`}>
            {status.text}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-ui-text2">{L.t('rulebook.list.intro')}</p>
          <button type="button" onClick={addNew} className={`${btn} bg-brand-dark font-medium text-white`}>+ {L.t('rulebook.list.add')}</button>
        </div>

        <ol className="grid gap-2">
          {rules.map((r, i) => {
            const off = r.enabled === false;
            return (
              <li key={`${r.rule_id}-${i}`} className={`rounded-xl border border-ui-border bg-ui-card p-3 sm:p-4 ${off ? 'opacity-60' : ''}`}>
                <div className="flex flex-wrap items-start gap-3">
                  <span className="mt-0.5 w-7 shrink-0 text-xs tabular-nums text-ui-text3">{i + 1}.</span>
                  <div className="min-w-0 flex-1 grid gap-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-sm text-ui-text break-all">{L.t(`rulebook.ruleNames.${r.rule_id}`, { defaultValue: r.rule_id })}</span>
                      {off && <span className="rounded-full border border-ui-border px-2 text-[11px] text-ui-text3">{L.t('rulebook.list.off')}</span>}
                    </div>
                    {r.description && <p className="text-xs text-ui-text3">{r.description}</p>}
                    <div className="grid gap-1">
                      <div className="flex flex-wrap items-baseline gap-2"><span className="text-[11px] uppercase tracking-wide text-ui-text3">{L.t('rulebook.list.when')}</span><ConditionChips when={r.when} /></div>
                      <div className="flex flex-wrap items-baseline gap-2"><span className="text-[11px] uppercase tracking-wide text-ui-text3">{L.t('rulebook.list.then')}</span><DecisionSummary d={r.decision} /></div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1 sm:ml-auto">
                    <label className="inline-flex items-center gap-1.5 px-2 text-xs text-ui-text2 min-h-11 cursor-pointer">
                      <input type="checkbox" className="h-4 w-4 accent-brand-dark" checked={!off} onChange={() => toggle(i)} aria-label={L.t('rulebook.list.toggle', { name: r.rule_id })} />
                      {L.t('rulebook.list.on')}
                    </label>
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className={`${btn} text-ui-text2 hover:bg-ui-hover disabled:opacity-30`} aria-label={L.t('rulebook.list.moveUp')}>↑</button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === rules.length - 1} className={`${btn} text-ui-text2 hover:bg-ui-hover disabled:opacity-30`} aria-label={L.t('rulebook.list.moveDown')}>↓</button>
                    <button type="button" onClick={() => setEditing({ rule: r, index: i })} className={`${btn} text-brand-dark hover:bg-brand-light/40`}>{L.t('rulebook.list.edit')}</button>
                    <button type="button" onClick={() => duplicate(i)} className={`${btn} text-ui-text2 hover:bg-ui-hover`}>{L.t('rulebook.list.duplicate')}</button>
                    {confirmDelete === r.rule_id ? (
                      <span className="inline-flex items-center gap-1">
                        <button type="button" onClick={() => remove(i)} className={`${btn} bg-danger text-white`}>{L.t('rulebook.list.confirmDelete')}</button>
                        <button type="button" onClick={() => setConfirmDelete(null)} className={`${btn} text-ui-text2`}>{L.t('rulebook.editor.cancel')}</button>
                      </span>
                    ) : (
                      <button type="button" onClick={() => setConfirmDelete(r.rule_id)} className={`${btn} text-danger hover:bg-red-50`}>{L.t('rulebook.list.delete')}</button>
                    )}
                  </div>
                </div>
                <div className="mt-1 sm:pl-10"><RuleExample ruleId={r.rule_id} /></div>
              </li>
            );
          })}
        </ol>

        <section className="rounded-xl border border-ui-border bg-ui-card p-4 grid gap-2">
          <h2 className="text-sm font-semibold text-ui-text">{L.t('rulebook.fallback.title')}</h2>
          <p className="text-xs text-ui-text3">{L.t('rulebook.fallback.help')}</p>
          <select
            id="rb-default-action"
            className="w-full sm:max-w-sm rounded-lg border border-ui-border bg-ui-card px-3 py-2 text-sm text-ui-text min-h-11"
            value={defaultDecision.action}
            onChange={(e) => setDefaultDecision({ action: e.target.value })}
          >
            {schema.actions.map((a) => <option key={a} value={a}>{L.action(a)}</option>)}
          </select>
        </section>
      </div>

      {editing && (
        <RuleEditor
          schema={schema}
          rule={editing.rule}
          isNew={editing.index == null}
          takenIds={rules.filter((_, k) => k !== editing.index).map((r) => r.rule_id)}
          onCancel={() => setEditing(null)}
          onApply={applyEdit}
        />
      )}
      {showHistory && <HistoryPanel onRestore={restore} onClose={() => setShowHistory(false)} />}
    </PageShell>
  );
};

export default RulebookPage;
